import {includes} from 'lodash';
import React from 'react';
import {Link} from 'react-router-dom';
import CollegeApplicationStatusMenu from 'shared/college-application-status/components/college-application-status-menu';
import CollegeCard from 'shared/components/college-card/college-card';
import Icon from 'shared/components/icon';
import Text from 'shared/components/text';
import EmptyCollegeCard from 'src/components/college-search/empty-college-card';
import LoadTrigger from 'src/components/load-trigger';
import useUserType from 'src/hooks/use-user-type/use-user-type';
import {CollegeSearchCollege} from 'src/redux/college-search/reducer';
import {UserType} from 'src/types/enums';
import {ApplicationStatusObject, CollegeSearch, ObjectOf} from 'src/types/store';
import 'stylesheets/components/college-search/college-search-filtered-colleges.scss';

interface Props {
  collegeSearch: CollegeSearch;
  followCollege: (collegeId: string) => void;
  unfollowCollege: (collegeId: string) => void;
  getColleges: (options?: {append: boolean}) => void;
  setCollegeAppStatus: (collegeId: string, status: string) => void;
  openTermNotOfferedModal: () => void;
  collegeApplicationStatuses: ObjectOf<ApplicationStatusObject>;
  showApplicationStatusMenu?: boolean;
}

const FilteredColleges = ({
  collegeSearch,
  followCollege,
  unfollowCollege,
  getColleges,
  setCollegeAppStatus,
  openTermNotOfferedModal,
  collegeApplicationStatuses,
  showApplicationStatusMenu,
}: Props) => {
  const {userType, isEducator, isHighSchool} = useUserType();

  const showDeadline = (college: CollegeSearchCollege): boolean => {
    const statusType = college.earningRelationship?.statusType;
    const {
      user: {highSchoolInfo},
    } = collegeSearch;
    const isHsSenior = isHighSchool && highSchoolInfo?.senior;
    const showToCCStudent =
      userType === UserType.CC_STUDENT &&
      includes(['earning_deadline_soon', 'submission_period_deferred'], statusType);

    return isEducator || isHsSenior || showToCCStudent;
  };

  const loadMoreColleges = (): void => {
    if (collegeSearch.page === 0) {
      // we shouldn't be trying to load more colleges if we haven't loaded any yet
      return;
    }

    getColleges({append: true});
  };

  const {
    filteredColleges,
    hasMore,
    sortBy,
    loading,
    user: {communityCollegeInfo},
  } = collegeSearch;

  let display = (
    <div className="college-search-filtered-colleges-no-results">
      <div className="college-search-filtered-colleges-no-results-text">
        <Text weight={4} emphasis showOverflow>
          We couldn&#39;t find any matches.
        </Text>
        <Text weight={5} color="darkgrey" showOverflow>
          Try de-selecting some filters to see more results.
        </Text>
      </div>
      <EmptyCollegeCard />
    </div>
  );

  if (filteredColleges === null) {
    display = (
      <div className="college-search-spinner-container">
        <Icon iconType="spinner" className="rm-spin rm-2x" />
      </div>
    );
  } else if (filteredColleges.length > 0) {
    display = (
      <>
        {filteredColleges.map((college: CollegeSearchCollege) => {
          const collegeApplicationStatus = collegeApplicationStatuses[college._id];
          const isHsOrTransferStudent =
            userType === UserType.HS_STUDENT || userType === UserType.CC_STUDENT;

          return (
            <span
              className="college-search-filtered-colleges-card"
              key={college._id}
              data-testid="college-search-result"
            >
              {isHsOrTransferStudent && showApplicationStatusMenu && (
                <CollegeApplicationStatusMenu
                  collegeApplicationStatus={collegeApplicationStatus}
                  setCollegeAppStatus={setCollegeAppStatus}
                  college={college}
                  className="college-search-app-status-section"
                  menuButtonColor="primary"
                  showStatusContent={false}
                  segmentEventLocation="college search dropdown"
                />
              )}
              <Link to={`/edu/${college.slug}`}>
                <CollegeCard
                  college={college}
                  followCollege={followCollege}
                  unfollowCollege={unfollowCollege}
                  shortCard={userType === UserType.EDUCATOR}
                  showDeadline={showDeadline(college)}
                  showDeadlineYear={userType === UserType.CC_STUDENT}
                  showStatus={college.earningRelationship !== null}
                  showFollow={userType !== UserType.EDUCATOR}
                  showBadge
                  showSortInfo
                  sortBy={sortBy}
                  communityCollegeInfo={communityCollegeInfo}
                  openTermNotOfferedModal={openTermNotOfferedModal}
                  simplified
                  userType={userType}
                />
              </Link>
            </span>
          );
        })}
      </>
    );
  }

  return (
    <div className="college-search-filtered-colleges">
      <div className="college-search-filtered-colleges-cards">{display}</div>
      <LoadTrigger
        onLoadMore={loadMoreColleges}
        buttonText="Load more colleges"
        isLoading={loading}
        hasMore={hasMore}
      />
    </div>
  );
};

export default FilteredColleges;
